<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :style="{ width: '380px' }"
    :closable="true"
    :dismissableMask="true"
    class="buy-alert-dialog"
    header="🚀 Ready to Invest?"
  >
    <div class="dialog-content">
      <!-- Investment Summary -->
      <div class="investment-summary">
        <div class="summary-header">
          <div class="check-icon">
            <i class="pi pi-check"></i>
          </div>
          <span class="summary-text">Great Choice! Your allocation looks perfect.</span>
        </div>

        <div class="investment-info">
          <div class="investment-name">{{ investmentData.securityName }} - {{ investmentData.assetClass }}</div>
          <div class="recommended-amount">
            ₹{{ formatAmount(investmentData.allocatedAmount) }}
            <span class="percentage">({{ investmentData.allocationPercentage }}%)</span>
          </div>
        </div>
      </div>

      <!-- Broker Integration Notice -->
      <div class="broker-notice">
        <i class="pi pi-info-circle"></i>
        <span>We're working on broker integrations. Meanwhile, our experts can help execute this investment.</span>
      </div>

      <!-- Contact Options -->
      <div class="contact-options">
        <div class="contact-title">Contact Our Investment Team</div>
        <div class="contact-methods">
          <div class="contact-method">
            <i class="pi pi-phone"></i>
            <span>+91-XXXX-XXXXXX</span>
          </div>
          <div class="contact-method">
            <i class="pi pi-envelope"></i>
            <span>invest@company.com</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <ANButton
          label="Maybe Later"
          variant="secondary"
          size="small"
          @click="closeDialog"
        />
        <ANButton
          label="Contact Investment Team"
          variant="primary"
          size="small"
          @click="contactTeam"
        />
      </div>
    </template>
  </Dialog>
</template><script setup>
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import ANButton from '@/components/common/AnButton.vue'

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  investmentData: {
    type: Object,
    default: () => ({
      securityName: '',
      assetClass: '',
      allocatedAmount: 0,
      allocationPercentage: '0.0'
    })
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'contact-team'])

// Computed
const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Methods
const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN').format(amount)
}

const closeDialog = () => {
  isVisible.value = false
}

const contactTeam = () => {
  emit('contact-team')
  isVisible.value = false
}
</script>

<style scoped>
.buy-alert-dialog {
  font-family: inherit;
}

.dialog-content {
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.investment-summary {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #d1fae5;
}

.check-icon {
  width: 26px;
  height: 26px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.check-icon i {
  font-size: 0.8rem;
  color: white;
}

.summary-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: #166534;
  line-height: 1.3;
}

.investment-info {
  padding: 18px 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.investment-name {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  margin-bottom: 10px;
  line-height: 1.3;
}

.recommended-amount {
  font-size: 1.15rem;
  font-weight: 700;
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 8px;
}

.percentage {
  font-size: 0.95rem;
  font-weight: 600;
  background: #ecfdf5;
  color: #047857;
  padding: 4px 8px;
  border-radius: 4px;
}

.broker-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fef3c7;
  border-radius: 6px;
  border: 1px solid #f59e0b;
}

.broker-notice i {
  color: #d97706;
  margin-top: 3px;
  flex-shrink: 0;
  font-size: 0.85rem;
}

.broker-notice span {
  font-size: 0.85rem;
  color: #92400e;
  line-height: 1.4;
}

.contact-options {
  text-align: center;
  padding: 0 4px;
}

.contact-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin-bottom: 14px;
}

.contact-methods {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-method {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: var(--an-text-secondary);
}

.contact-method i {
  color: var(--an-primary);
  font-size: 0.8rem;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 8px 0 0 0;
}

.dialog-actions .an-button {
  flex: 1;
  padding: 12px 16px !important;
  font-size: 0.9rem !important;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .buy-alert-dialog {
    width: 95vw !important;
  }

  .dialog-content {
    gap: 14px;
  }

  .summary-header {
    padding: 10px 14px;
  }

  .investment-info {
    padding: 14px;
  }

  .broker-notice {
    padding: 8px 10px;
  }

  .contact-methods {
    gap: 4px;
  }

  .contact-method {
    padding: 6px 10px;
    font-size: 0.75rem;
  }

  .dialog-actions {
    flex-direction: column;
    gap: 6px;
  }
}
</style>
